import React, { useState, useEffect } from 'react';
import { Card, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import './HallOfFame.css';

const HallOfFame = ({
    selectedYear,
    championTeam = [],
    championStats = {},
    loadingYearData = false
}) => {
    const [historicData, setHistoricData] = useState(null);

    // Load data.json for historic years (before 2022)
    useEffect(() => {
        fetch('/data.json')
            .then(response => response.json())
            .then(jsonData => {
                setHistoricData(jsonData);
            })
            .catch(err => {
                console.error('Error loading historic data:', err);
            });
    }, []);

    // Simplified medal classes
    const getMedalClass = (rank) => {
        switch (rank) {
            case 'Nhất': return 'gold';
            case 'Nhì': return 'silver';
            case 'Ba': return 'bronze';
            default: return '';
        }
    };

    const getSerieColor = (serie) => {
        if (serie.includes('A')) return 'serie-a';
        if (serie.includes('B')) return 'serie-b';
        if (serie.includes('C')) return 'serie-c';
        return 'serie-default';
    };

    // Determine whether to use API data or JSON data
    // Prioritize data.json if available (has complete data), otherwise use API
    const hasJsonData = historicData && historicData[selectedYear];
    const isCovid = selectedYear === '2021';

    // Get data for the selected year
    const getCurrentYearData = () => {
        // First check if we have data in data.json (has complete Nhất, Nhì, Ba)
        if (hasJsonData) {
            return historicData[selectedYear];
        }

        // Fall back to API data for years not in data.json
        if (championTeam.length > 0) {
            // Transform API data to the same format as JSON data
            const transformed = {};

            championTeam.forEach(champion => {
                const serieName = champion.level_team_name || 'Serie A';
                if (!transformed[serieName]) {
                    transformed[serieName] = {
                        'Nhất': champion.champion_team_name || 'N/A',
                        'Nhì': champion.second_team_name || 'N/A',
                        'Ba': champion.third_team_name || 'N/A',
                        // Store additional data for API teams
                        _apiData: {
                            first: {
                                logo: champion.champion_team_logo,
                                teamId: champion.champion_team_id
                            },
                            second: {
                                logo: champion.second_team_logo,
                                teamId: champion.second_team_id
                            },
                            third: {
                                logo: champion.third_team_logo,
                                teamId: champion.third_team_id
                            },
                            // Joint-third: 2 semi-final losers when no 3rd-place match
                            thirdList: Array.isArray(champion.third_teams) && champion.third_teams.length > 0
                                ? champion.third_teams
                                : null
                        }
                    };
                }
            });

            return transformed;
        }

        return null;
    };

    const currentYearData = getCurrentYearData();

    if (loadingYearData && !currentYearData) {
        return (
            <div className="hof-section-light">
                <div className="hof-loading-light">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Đang tải dữ liệu...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="hof-section-light mb-4">
            {/* Champions Display */}
            <div className="hof-content-light">
                {loadingYearData ? (
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2 text-muted">Đang tải dữ liệu năm {selectedYear}...</p>
                    </div>
                ) : isCovid ? (
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="text-start py-5 px-4 px-md-5">
                            <h4 className="text-muted mt-3 fw-bold">Năm 2021 - Nghỉ thi đấu</h4>
                            <p className="text-muted mb-0 small">Do ảnh hưởng của đại dịch COVID-19, giải đấu năm 2021 đã tạm hoãn.</p>
                        </Card.Body>
                    </Card>
                ) : currentYearData ? (
                    <div className="series-container">
                        {(() => {
                            // Sort series: B - A - C (A in the middle)
                            const seriesEntries = Object.entries(currentYearData)
                                .filter(([key]) => !key.startsWith('_'));

                            const sortOrder = { 'Serie B': 1, 'Series B': 1, 'Serie A': 2, 'Series A': 2, 'Serie C': 3, 'Series C': 3 };
                            seriesEntries.sort((a, b) => {
                                const orderA = sortOrder[a[0]] || 99;
                                const orderB = sortOrder[b[0]] || 99;
                                return orderA - orderB;
                            });

                            const seriesCount = seriesEntries.length;

                            return seriesEntries.map(([serieName, results]) => {
                                const apiData = results._apiData;
                                const isSerieA = serieName.includes('A');

                                return (
                                    <div key={serieName} className={`serie-wrapper serie-count-${seriesCount} ${isSerieA ? 'serie-a-featured' : ''}`}>
                                        <Card className={`h-100 border-0 shadow-sm serie-card-light ${getSerieColor(serieName)} ${isSerieA ? 'serie-a-card' : ''}`} style={{ borderRadius: '16px' }}>
                                            <Card.Header className={`bg-transparent border-bottom py-3 ${isSerieA ? 'serie-a-header' : ''}`}>
                                                <Badge className={`serie-badge-light ${getSerieColor(serieName)} ${isSerieA ? 'serie-a-badge' : ''}`}>
                                                    {serieName}
                                                </Badge>
                                            </Card.Header>
                                            <Card.Body className="p-0">
                                                {Object.entries(results).map(([rank, teamName]) => {
                                                    // Skip internal data keys
                                                    if (rank.startsWith('_')) return null;

                                                    // Joint-third: 2 semi-final losers (no 3rd-place match)
                                                    const jointThird = rank === 'Ba' && apiData?.thirdList?.length > 1
                                                        ? apiData.thirdList
                                                        : null;
                                                    if (jointThird) {
                                                        return (
                                                            <div key={rank} className={`champion-row-light ${getMedalClass(rank)}`}>
                                                                <div className="medal-container-light">
                                                                    <div className={`medal-indicator ${getMedalClass(rank)}`}></div>
                                                                </div>
                                                                <div className="team-info-light">
                                                                    <span className="rank-label-light">{rank} (đồng hạng)</span>
                                                                    <span className="team-name-light">
                                                                        {jointThird.map((t, idx) => (
                                                                            <span key={t.id || idx}>
                                                                                {idx > 0 && ' / '}
                                                                                {t.logo && (
                                                                                    <img
                                                                                        src={t.logo}
                                                                                        alt={t.name}
                                                                                        className="team-logo-light"
                                                                                        style={{ display: 'inline-block', verticalAlign: 'middle' }}
                                                                                    />
                                                                                )}
                                                                                {t.id ? (
                                                                                    <Link to={`/teams/${t.id}`} className="team-link-light">
                                                                                        {t.name}
                                                                                    </Link>
                                                                                ) : (
                                                                                    t.name
                                                                                )}
                                                                            </span>
                                                                        ))}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        );
                                                    }

                                                    // For API data, get additional info based on rank
                                                    let logo = null;
                                                    let teamId = null;

                                                    if (apiData) {
                                                        const rankKey = rank === 'Nhất' ? 'first' : rank === 'Nhì' ? 'second' : rank === 'Ba' ? 'third' : null;
                                                        if (rankKey && apiData[rankKey]) {
                                                            logo = apiData[rankKey].logo;
                                                            teamId = apiData[rankKey].teamId;
                                                        }
                                                    }

                                                    return (
                                                        <div key={rank} className={`champion-row-light ${getMedalClass(rank)}`}>
                                                            <div className="medal-container-light">
                                                                <div className={`medal-indicator ${getMedalClass(rank)}`}></div>
                                                            </div>
                                                            {logo && (
                                                                <img
                                                                    src={logo}
                                                                    alt={teamName}
                                                                    className="team-logo-light"
                                                                />
                                                            )}
                                                            <div className="team-info-light">
                                                                <span className="rank-label-light">{rank}</span>
                                                                <span className="team-name-light">
                                                                    {teamId ? (
                                                                        <Link to={`/teams/${teamId}`} className="team-link-light">
                                                                            {teamName}
                                                                        </Link>
                                                                    ) : (
                                                                        teamName
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </Card.Body>
                                        </Card>
                                    </div>
                                );
                            });
                        })()}
                    </div>
                ) : (
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="text-start py-5 px-4 px-md-5">
                            <h5 className="text-muted">Chưa có thông tin cho năm {selectedYear}</h5>
                        </Card.Body>
                    </Card>
                )}
            </div>
        </div>
    );
};

export default HallOfFame;
